import { StandardProps } from "@/components/componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant";
import styles from "./ProductAccordion.module.scss";
import { ProductDescriptionAccordionUI } from "./components/ProductDescriptionAccordion/ProductDescriptionAccordionUI";
import { getDescription } from "@/utils/product/getDescription";
import { ProductSpecificationAccordionUI } from "./components/ProductSpecificationAccordion/ProductSpecificationAccordionUI";
import { sortBy } from "@/utils/sortBy/sortBy";
import { ProductBrandAccordionUI } from "./components/ProductBrandAccordion/ProductBrandAccordionUI";
import { ProductDeliveryOptionsAccordionUI } from "./components/ProductDeliveryOptionsAccordion/ProductDeliveryOptionsAccordionUI";
import { isAvailableToPreOrder } from "@/utils/variant/isAvailableToPreOrder";
import {
  Attribute,
  AvailableOption,
  UnavailableCollectionOption,
} from "../../types/prodcatBaseTypes";
import { getDeliveryPrice } from "./components/ProductDeliveryOptionsAccordion/util/getDeliveryPrice";
import { ProductReviewsAccordionUI } from "./components/ProductReviewsAccordion/ProductReviewsAccordionUI";
import { useRouter } from "next/router";
import { ensureIsActive } from "@/utils/product/ensureIsActive";
import { isInStoreOnly } from "@/utils/variant/isInStoreOnly";
import { ProductSizeAndFitAccordionUI } from "./components/ProductSizeAndFitAccordion/ProductSizeAndFitAccordionUI";
import { getSizeAndFitAttributes } from "./components/ProductSizeAndFitAccordion/sizeAndFitUtils";
import { BuyingGuidesAccordionUI } from "./components/BuyingGuidesAccordion/BuyingGuidesUI";
import { OutletAccordionUI } from "./components/OutletAccordion/OutletAccordionUI";
import { ProductHowToUseAccordionUI } from "./components/HowToUseAccordion/HowToUseAccordionUI";
import { ProductIngredientsAccordionUI } from "./components/IngredientsAccordion/IngredientsAccordionUI";
import { ProductMeasurementsAccordionUI } from "./components/ProductMeasurementsAccordion/ProductMeasurementsAccordionUI";
import { isOutlet } from "@/utils/variant/isOutlet";
import { getBaseUrlAndQuery } from "@/utils/url/getBaseUrlAndQuery";
import { isMadeToMeasure } from "@/utils/product/isMadeToMeasure";
import { isPerMetre } from "@/utils/product/isPerMetre";
import { Accordion } from "@JohnLewisPartnership/jl-design-system/dist/components/accordion/Accordion";
import classNames from "classnames/bind";
import {
  AccordionConfiguration,
  productAccordionConfigSchema,
} from "./config/productAccordionConfigSchema";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { useInView } from "react-intersection-observer";
import { getMeasurementsAttributes } from "./components/ProductMeasurementsAccordion/measurementsUtils";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { useEffect } from "react";

const cx = classNames.bind(styles);

const defaultAccordions: AccordionConfiguration = [
  {
    name: "description",
    open: false,
  },
  {
    name: "specification",
    open: false,
  },
  {
    name: "brand",
    open: false,
  },
  {
    name: "deliveryOptions",
    open: false,
  },
];

const transformAvailable = (option: AvailableOption) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: getDeliveryPrice(option),
});

const transformUnavailableCollection = (
  option: UnavailableCollectionOption,
) => ({
  headline: option.headline,
  description: option.description,
  displayPrice: "",
});

const validateConfigFromProps = (
  configuration: unknown,
): AccordionConfiguration | null => {
  const parsedConfig = productAccordionConfigSchema.safeParse(configuration);
  if (parsedConfig.success) {
    return parsedConfig.data;
  }
  return null;
};

export const ProductAccordion = ({
  product,
  runtimeConfig,
  configProps,
  experiencesRawObject,
  flags,
}: Pick<
  StandardProps,
  "product" | "runtimeConfig" | "configProps" | "experiencesRawObject" | "flags"
>) => {
  const { variant } = useVariant(product);

  const showOutletReturnMessage = isOutlet(variant);
  const isRetired = !ensureIsActive(product);
  const isM2M = isMadeToMeasure(product);
  const isMeteredProduct = isPerMetre(product);
  const simplifiedBeauty = configProps?.simplifiedBeauty === true;
  const showGalleryImage = flags.showAccordionGalleryImage.value === true;
  const { monetateId } = useMonetateId();
  const { asPath } = useRouter();

  const screenWidthMin =
    Number(flags.accordionGalleryImageScreenWidthMin?.value) || 0;
  const screenWidthMaxRaw = Number(
    flags.accordionGalleryImageScreenWidthMax?.value,
  );
  const screenWidthMax = screenWidthMaxRaw > 0 ? screenWidthMaxRaw : null;

  useEffect(() => {
    if (screenWidthMin > 0) {
      const screenWidth = window.screen.width;
      const inRange =
        screenWidth >= screenWidthMin &&
        (screenWidthMax === null || screenWidth <= screenWidthMax);
      recordImpressionByParam(
        runtimeConfig?.monetateEnvironment,
        experiencesRawObject,
        "pdp-scaffold-showAccordionGalleryImageImpression",
        inRange ? "true" : "false",
        monetateId,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { ref } = useInView({
    rootMargin: "0px 0px -50px 0px",
    triggerOnce: true,
    onChange: (inView) => {
      if (inView) {
        dispatchMonetateQEvent("pdp:product-accordion:visible");
      }
    },
  });

  // Hide component when RHR layout is active ("rhr")
  // Show when flag is empty/default (coexistence layout) or "accordion" (isolated accordion layout)
  const flagValue = flags.enablePremiumRhrSideDrawers.value;
  if (flagValue === "rhr") {
    return null;
  }

  const [endorsement] =
    variant.messaging?.filter((message) => message.type === "endorsement") ||
    [];

  const variantAttributes = variant.attributes || [];
  const primaryAttributes = product.attributes || [];
  const allAttributes = [...primaryAttributes, ...variantAttributes];
  const sortedAttributes = sortBy(allAttributes, (a) => a.displayName || "");

  const available = product.fulfilmentOptions?.available || [];
  const unavailableCollection =
    product.fulfilmentOptions?.unavailableCollection || [];

  let deliveryOptions = [
    ...available.map(transformAvailable),
    ...unavailableCollection.map(transformUnavailableCollection),
  ];

  if (isM2M) {
    deliveryOptions = deliveryOptions.map((option) => ({
      ...option,
      displayPrice: "Free",
    }));
  }

  const measurementsAttributes = getMeasurementsAttributes(
    allAttributes,
    product.sellingProductType,
  );

  const { reviewsApiKey, reviewsScriptUrl } = runtimeConfig;

  const displayAccordions = (
    validateConfigFromProps(configProps?.accordionConfig) || defaultAccordions
  ).map((accordion) => ({
    ...accordion,
    open: false,
  }));

  let productSpecificationAttributes = sortedAttributes;
  let sizeAndFitAttributes: Attribute[] = [];

  if (displayAccordions.some((da) => da.name === "sizeAndFit")) {
    const categoryIds = product?.browsePageHierarchy?.map(
      (bph) => bph.categoryId,
    );
    sizeAndFitAttributes = getSizeAndFitAttributes(
      categoryIds,
      sortedAttributes,
    );

    if (sizeAndFitAttributes?.length > 0) {
      productSpecificationAttributes = sortedAttributes.filter(
        (attr) => !sizeAndFitAttributes.includes(attr),
      );
    }
  }

  const { baseUrl } = getBaseUrlAndQuery(asPath);

  const accordionClasses = cx(styles.container, {
    simplifiedBeauty: simplifiedBeauty,
    withGalleryImage: showGalleryImage,
  });

  return (
    <div
      ref={ref}
      data-testid="product:accordion"
      data-component="ProductAccordion"
      className={accordionClasses}
    >
      <div className={styles.accordionStack}>
        <Accordion>
          {displayAccordions.map((accordion) => {
            switch (accordion.name) {
              case "description":
                return (
                  <ProductDescriptionAccordionUI
                    key={accordion.name}
                    productType={product.__typename}
                    displayCode={variant.displayCode}
                    description={getDescription(product)}
                    icons={product.icons || []}
                    links={product.links || []}
                    endorsement={endorsement}
                    open={accordion.open}
                    hideDescriptionLinks={
                      displayAccordions.find(
                        (a) => a.name === "buyingGuides",
                      ) !== undefined
                    }
                    showBeautySocietyLink={
                      configProps?.beautySocietyLink === true
                    }
                  />
                );
              case "buyingGuides":
                return (
                  <BuyingGuidesAccordionUI
                    key={accordion.name}
                    links={product.links || []}
                    open={accordion.open}
                  />
                );
              case "specification":
                return (
                  <ProductSpecificationAccordionUI
                    key={accordion.name}
                    attributes={productSpecificationAttributes}
                    productType={product.__typename}
                    open={accordion.open}
                  />
                );
              case "howToUse":
                return (
                  <ProductHowToUseAccordionUI
                    key={accordion.name}
                    open={accordion.open}
                    howtoUseContent={product?.howToUse}
                  />
                );
              case "ingredients":
                return (
                  <ProductIngredientsAccordionUI
                    key={accordion.name}
                    product={product}
                    open={accordion.open}
                    runtimeConfig={runtimeConfig}
                    experiencesRawObject={experiencesRawObject}
                    flags={flags}
                  />
                );
              case "brand":
                return (
                  <ProductBrandAccordionUI
                    key={accordion.name}
                    brand={product.brand}
                    sizeGuide={product.sizeGuide}
                    open={accordion.open}
                    disableSizeGuide={configProps?.disableSizeGuide === true}
                  />
                );
              case "outlet":
                return (
                  <OutletAccordionUI
                    key={accordion.name}
                    open={accordion.open}
                  />
                );
              case "deliveryOptions":
                return (
                  <ProductDeliveryOptionsAccordionUI
                    key={accordion.name}
                    isBladedArticle={
                      variant.sellingRestrictions?.age?.bladedArticle ?? false
                    }
                    deliveryOptions={deliveryOptions}
                    skuId={variant.aliases.skuId}
                    isAvailableToOrderOrPreorder={
                      variant.availability?.availableToOrder ||
                      isAvailableToPreOrder(variant)
                    }
                    stockApiBaseUrl={runtimeConfig.stockApiBaseUrl}
                    stockApiKey={runtimeConfig.stockApiKey}
                    showExtendedReturnsMessaging={
                      flags.showExtendedReturnsMessaging.value === true
                    }
                    extendedReturnsDates={{
                      startDate: String(
                        flags.extendedReturnsPeriodStartDate.value,
                      ),
                      endDate: String(flags.extendedReturnsPeriodEndDate.value),
                      deadlineDate: String(
                        flags.extendedReturnsDeadlineDate.value,
                      ),
                    }}
                    isInStoreOnly={isInStoreOnly(variant)}
                    isRetired={isRetired}
                    isM2M={isM2M}
                    isMeteredProduct={isMeteredProduct}
                    open={accordion.open}
                    browsePageHierarchy={product.browsePageHierarchy}
                    sellingProductType={product.sellingProductType}
                    showOutletReturnMessage={showOutletReturnMessage}
                    returnsStartDate="2024-09-26"
                    returnsEndDate="2025-01-23"
                    showChristmasDeliveryMessaging={
                      flags.showChristmasDeliveryMessaging.value === true
                    }
                  />
                );
              case "reviews":
                return (
                  <ProductReviewsAccordionUI
                    key={accordion.name}
                    productId={product.aliases.pimProductId}
                    reviewsApiKey={reviewsApiKey}
                    reviewsScriptUrl={reviewsScriptUrl}
                    reviewSummary={product?.reviewSummary}
                    productUrl={baseUrl}
                    open={accordion.open}
                    experiencesRawObject={experiencesRawObject}
                  />
                );
              case "sizeAndFit":
                return (
                  <ProductSizeAndFitAccordionUI
                    key={accordion.name}
                    attributes={sizeAndFitAttributes}
                    productType={product.__typename}
                    open={accordion.open}
                  />
                );
              case "specialOffers":
                return null;
              case "measurements":
                return (
                  measurementsAttributes.length > 0 &&
                  product.sellingProductType && (
                    <ProductMeasurementsAccordionUI
                      key={accordion.name}
                      attributes={measurementsAttributes}
                      open={accordion.open}
                    />
                  )
                );
              default:
                return null;
            }
          })}
        </Accordion>
      </div>
      {showGalleryImage && (
        <div className={styles.galleryImageWrapper}>
          <img
            data-testid="product:accordion:gallery-image"
            src={variant.images?.primary}
            alt=""
          />
        </div>
      )}
    </div>
  );
};
